import type { SortItem } from './types';
import { SortItemFactory } from './SortItemFactory';

export class DatasetGenerator {
  static random(count: number, min = 0, max = 100): SortItem[] {
    const values = Array.from({ length: count }, () =>
      Math.floor(Math.random() * (max - min + 1)) + min
    );
    return SortItemFactory.createArray(values);
  }

  static alreadySorted(count: number, min = 0, max = 100): SortItem[] {
    const values = Array.from({ length: count }, (_, i) =>
      min + Math.floor((max - min) * (i / (count - 1)))
    );
    return SortItemFactory.createArray(values);
  }

  static reverseSorted(count: number, min = 0, max = 100): SortItem[] {
    const values = Array.from({ length: count }, (_, i) =>
      max - Math.floor((max - min) * (i / (count - 1)))
    );
    return SortItemFactory.createArray(values);
  }

  static nearlySorted(count: number, min = 0, max = 100, swaps = 3): SortItem[] {
    const values = this.alreadySorted(count, min, max).map(v => v.value);
    for (let i = 0; i < swaps; i++) {
      const idx1 = Math.floor(Math.random() * count);
      const idx2 = Math.floor(Math.random() * count);
      [values[idx1], values[idx2]] = [values[idx2], values[idx1]];
    }
    return SortItemFactory.createArray(values);
  }

  static fewUniqueValues(count: number, unique = 5, min = 0, max = 100): SortItem[] {
    const step = Math.floor((max - min + 1) / unique);
    const values = Array.from({ length: count }, () => {
      const bucket = Math.floor(Math.random() * unique);
      return min + bucket * step + Math.floor(Math.random() * step);
    });
    return SortItemFactory.createArray(values);
  }

  static manyDuplicates(count: number, duplicates = 3, min = 0, max = 100): SortItem[] {
    const step = Math.floor((max - min + 1) / duplicates);
    const values = Array.from({ length: count }, () => {
      const duplicate = Math.floor(Math.random() * duplicates);
      return min + duplicate * step;
    });
    return SortItemFactory.createArray(values);
  }

  static clustered(count: number, clusters = 3, min = 0, max = 100): SortItem[] {
    const clusterSpacing = Math.floor((max - min + 1) / clusters);
    const values = Array.from({ length: count }, () => {
      const cluster = Math.floor(Math.random() * clusters);
      const clusterCenter = min + cluster * clusterSpacing + clusterSpacing / 2;
      return Math.round(clusterCenter + (Math.random() - 0.5) * (clusterSpacing * 0.4));
    });
    return SortItemFactory.createArray(values);
  }

  static uniform(count: number, min = 0, max = 100): SortItem[] {
    const values = Array.from({ length: count }, () => Math.round(Math.random() * (max - min) + min));
    return SortItemFactory.createArray(values);
  }

  static skewed(count: number, skewFactor = 2, min = 0, max = 100): SortItem[] {
    const values = Array.from({ length: count }, () => {
      const normalized = Math.random();
      const skewed = Math.pow(normalized, skewFactor);
      return Math.round(min + skewed * (max - min));
    });
    return SortItemFactory.createArray(values);
  }

  static outlierHeavy(count: number, outlierRatio = 0.2, min = 0, max = 100): SortItem[] {
    const values = Array.from({ length: count }, () => {
      if (Math.random() < outlierRatio) {
        return Math.round(Math.random() * (max - min) + min);
      }
      const range = (max - min) * (1 - outlierRatio);
      return Math.round(Math.random() * range + min);
    });
    return SortItemFactory.createArray(values);
  }

  static adversarialQuickSort(count: number, min = 0, max = 100): SortItem[] {
    const values = Array.from({ length: count }, (_, i) => min + Math.round(i * ((max - min) / (count - 1))));
    return SortItemFactory.createArray(values);
  }

  static complexPattern(count: number): SortItem[] {
    const base = Math.floor(Math.sqrt(count));
    const values = Array.from({ length: count }, (_, i) =>
      Math.round((Math.sin(i / base) * 50) + (Math.cos(i / (base * 2)) * 30) + 50)
    );
    return SortItemFactory.createArray(values);
  }

  static getPresetNames(): string[] {
    return [
      'random', 'already_sorted', 'reverse_sorted', 'nearly_sorted',
      'few_unique_values', 'many_duplicates', 'clustered', 'uniform',
      'skewed', 'outlier_heavy', 'adversarial_quick_sort', 'complex_pattern'
    ];
  }

  static getPresetConfig(name: string): { generator: (count: number) => SortItem[]; count: number; description: string } {
    const configs: Record<string, { generator: (count: number) => SortItem[]; count: number; description: string }> = {
      random: { generator: (c) => this.random(c), count: 20, description: 'Random order of elements' },
      already_sorted: { generator: (c) => this.alreadySorted(c), count: 50, description: 'Already sorted in ascending order' },
      reverse_sorted: { generator: (c) => this.reverseSorted(c), count: 50, description: 'Sorted in descending order (worst for Quick Sort)' },
      nearly_sorted: { generator: (c) => this.nearlySorted(c), count: 50, description: 'Mostly sorted with few random elements' },
      few_unique_values: { generator: (c) => this.fewUniqueValues(c), count: 50, description: 'Array with few unique values' },
      many_duplicates: { generator: (c) => this.manyDuplicates(c), count: 50, description: 'Array with many duplicate values' },
      clustered: { generator: (c) => this.clustered(c), count: 50, description: 'Data clustered in groups' },
      uniform: { generator: (c) => this.uniform(c), count: 50, description: 'Uniformly distributed values' },
      skewed: { generator: (c) => this.skewed(c), count: 50, description: 'Skewed distribution toward one side' },
      outlier_heavy: { generator: (c) => this.outlierHeavy(c), count: 50, description: 'Array with heavy outliers' },
      adversarial_quick_sort: { generator: (c) => this.adversarialQuickSort(c), count: 50, description: 'Worst-case data for Quick Sort' },
      complex_pattern: { generator: (c) => this.complexPattern(c), count: 50, description: 'Complex natural pattern' }
    };
    return configs[name] ?? configs.random;
  }
}
